/* Defy The Norm / collection page: filters, sort, density, load more and quick add.
   Filters, sort and pagination refresh the section through the Section Rendering API,
   so the page never reloads and the URL always matches what is on screen. */

const DTN_STORE = {
  get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage unavailable */ } }
};

class DtnCollection extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.root = this.closest('.dtn-coll') || this;
    this.root.setAttribute('data-js', '');
    this.sectionId = this.dataset.section;
    this.baseUrl = this.dataset.url || window.location.pathname;
    this.mq = window.matchMedia('(max-width: 989px)');

    // Remembered layout choices
    const side = DTN_STORE.get('dtn-coll-side');
    if (side === 'hidden') this.setSide(false, false);
    const cols = DTN_STORE.get('dtn-coll-cols');
    if (cols === '3' || cols === '4') this.setCols(cols, false);
    const colsM = DTN_STORE.get('dtn-coll-cols-m');
    if (colsM === '1' || colsM === '2') this.setColsM(colsM, false);

    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('input', (e) => { if (e.target.matches('.dtn-coll__field input')) this.queue(900); });
    this.addEventListener('submit', (e) => { if (e.target.matches('[data-filters]')) { e.preventDefault(); this.queue(0); } });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.root.classList.contains('is-drawer')) this.closeDrawer(); });
    window.addEventListener('popstate', (e) => { if (e.state && e.state.dtnColl) this.render(window.location.href, false); });
    if (!window.history.state || !window.history.state.dtnColl) window.history.replaceState({ dtnColl: true }, '', window.location.href);
  }

  /* ---------- Events ---------- */
  onClick(e) {
    const t = e.target;
    const toggle = t.closest('[data-filter-toggle]');
    if (toggle) {
      if (this.mq.matches) this.openDrawer();
      else this.setSide(this.root.dataset.side === 'hidden', true);
      return;
    }
    if (t.closest('[data-filter-close]')) { this.closeDrawer(); return; }
    const c = t.closest('[data-cols-set]');
    if (c) { this.setCols(c.dataset.colsSet, true); return; }
    const cm = t.closest('[data-cols-m]');
    if (cm) { this.setColsM(cm.dataset.colsM, true); return; }
    const ajax = t.closest('a[data-ajax]');
    if (ajax) { e.preventDefault(); this.render(ajax.href, true); return; }
    const more = t.closest('[data-load-more]');
    if (more) { e.preventDefault(); this.loadMore(more); return; }
    const open = t.closest('[data-qa-open]');
    if (open) { e.preventDefault(); this.toggleQuick(open); return; }
    const add = t.closest('[data-qa-add]');
    if (add) { e.preventDefault(); this.quickAdd(add); }
  }

  onChange(e) {
    const t = e.target;
    if (t.matches('[data-sort]')) { this.setSort(t.value); this.queue(0); return; }
    if (t.matches('[data-sort-radio]')) { this.setSort(t.value); this.queue(0); return; }
    if (t.closest('[data-filters]')) this.queue(t.matches('.dtn-coll__field input') ? 300 : 250);
  }

  setSort(v) {
    const hidden = this.querySelector('[data-sort-input]');
    if (hidden) hidden.value = v;
    const sel = this.querySelector('[data-sort]');
    if (sel && sel.value !== v) sel.value = v;
    this.querySelectorAll('[data-sort-radio]').forEach((r) => { r.checked = r.value === v; });
  }

  queue(ms) {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.render(this.formUrl(), true), ms);
  }

  formUrl() {
    const form = this.querySelector('[data-filters]');
    const params = new URLSearchParams();
    if (form) {
      new FormData(form).forEach((v, k) => {
        if (k === 'dtn-sort-m' || v === '' || v == null) return;
        params.append(k, v);
      });
    }
    const q = params.toString();
    return this.baseUrl + (q ? '?' + q : '');
  }

  /* ---------- Layout ---------- */
  setSide(show, remember) {
    this.root.dataset.side = show ? 'shown' : 'hidden';
    const btn = this.querySelector('[data-filter-toggle]');
    const label = this.querySelector('[data-filter-label]');
    if (btn) btn.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (label) label.textContent = show ? label.dataset.hide : label.dataset.show;
    if (remember) DTN_STORE.set('dtn-coll-side', show ? 'shown' : 'hidden');
  }
  setCols(n, remember) {
    this.style.setProperty('--c-user', n);
    this.querySelectorAll('[data-cols-set]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.colsSet === String(n) ? 'true' : 'false'));
    if (remember) DTN_STORE.set('dtn-coll-cols', String(n));
  }
  setColsM(n, remember) {
    this.style.setProperty('--cols-m', n);
    this.querySelectorAll('[data-cols-m]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.colsM === String(n) ? 'true' : 'false'));
    if (remember) DTN_STORE.set('dtn-coll-cols-m', String(n));
  }
  openDrawer() {
    this.root.classList.add('is-drawer');
    document.documentElement.style.overflow = 'hidden';
    this.querySelector('[data-filter-toggle]')?.setAttribute('aria-expanded', 'true');
    const side = this.querySelector('[data-side]');
    side?.setAttribute('role', 'dialog');
    side?.setAttribute('aria-modal', 'true');
    requestAnimationFrame(() => this.querySelector('.dtn-coll__x')?.focus());
  }
  closeDrawer() {
    if (!this.root.classList.contains('is-drawer')) return;
    this.root.classList.remove('is-drawer');
    document.documentElement.style.overflow = '';
    const side = this.querySelector('[data-side]');
    side?.removeAttribute('role');
    side?.removeAttribute('aria-modal');
    const btn = this.querySelector('[data-filter-toggle]');
    btn?.setAttribute('aria-expanded', 'false');
    btn?.focus();
  }

  /* ---------- Rendering ---------- */
  sectionUrl(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.toString();
  }

  async fetchSection(url) {
    const res = await fetch(this.sectionUrl(url), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(res.status);
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html').querySelector('dtn-collection');
  }

  cleanUrl(url) {
    // Filter and sort changes always start again at page 1; section_id never belongs in the address bar
    const u = new URL(url, window.location.origin);
    u.searchParams.delete('section_id');
    u.searchParams.delete('page');
    return u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '');
  }

  async render(rawUrl, push) {
    const url = this.cleanUrl(rawUrl);
    const token = (this._token = (this._token || 0) + 1);
    this.root.setAttribute('data-busy', '');
    // Remember which control had focus so keyboard users keep their place
    const a = document.activeElement;
    const focusKey = a && this.contains(a) && a.name ? a.name + '|' + a.value : null;
    try {
      const fresh = await this.fetchSection(url);
      if (token !== this._token || !fresh) return;
      ['[data-pills]', '[data-count]', '[data-filter-groups]', '.dtn-coll__main'].forEach((sel) => {
        const now = this.querySelector(sel);
        const next = fresh.querySelector(sel);
        if (now && next) now.innerHTML = next.innerHTML;
      });
      const badge = this.querySelector('[data-active-count]');
      const nextBadge = fresh.querySelector('[data-active-count]');
      if (badge && nextBadge) { badge.textContent = nextBadge.textContent; badge.hidden = nextBadge.hidden; }
      const n = this.querySelector('[data-result-n]');
      const nn = fresh.querySelector('[data-result-n]');
      if (n && nn) n.textContent = nn.textContent;
      const sort = fresh.querySelector('[data-sort-input]');
      if (sort) this.setSort(sort.value);
      this.querySelectorAll('[data-cell]').forEach((c) => c.classList.add('is-new'));
      if (push) window.history.pushState({ dtnColl: true }, '', url);
      if (focusKey) {
        const [name, value] = focusKey.split('|');
        const el = [...this.querySelectorAll('[name]')].find((x) => x.name === name && x.value === value);
        el?.focus({ preventScroll: true });
      }
      // Keep the toolbar in view when results change below a long page
      if (!this.mq.matches) {
        const bar = this.querySelector('[data-bar]');
        const top = this.querySelector('[data-body]')?.getBoundingClientRect().top;
        if (bar && top < 0) window.scrollBy({ top: top - bar.offsetHeight, behavior: 'smooth' });
      }
    } catch (err) {
      window.location.href = url;
    } finally {
      if (token === this._token) this.root.removeAttribute('data-busy');
    }
  }

  async loadMore(link) {
    link.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.fetchSection(link.href);
      const grid = this.querySelector('[data-grid]');
      if (fresh && grid) {
        const cells = [...fresh.querySelectorAll('[data-grid] > [data-cell]')];
        const firstNew = cells[0];
        cells.forEach((c) => { c.classList.add('is-new'); grid.appendChild(c); });
        const pager = this.querySelector('[data-pager]');
        const nextPager = fresh.querySelector('[data-pager]');
        if (pager && nextPager) pager.innerHTML = nextPager.innerHTML;
        firstNew?.querySelector('a[href]')?.focus({ preventScroll: true });
      }
    } catch (err) {
      window.location.href = link.href;
    } finally {
      link.removeAttribute('aria-busy');
    }
  }

  /* ---------- Quick add ---------- */
  toggleQuick(btn) {
    const panel = btn.parentElement.querySelector('[data-qa]');
    const open = btn.getAttribute('aria-expanded') !== 'true';
    this.querySelectorAll('[data-qa-open][aria-expanded="true"]').forEach((b) => {
      if (b !== btn) { b.setAttribute('aria-expanded', 'false'); b.parentElement.querySelector('[data-qa]')?.classList.remove('is-open'); }
    });
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel?.classList.toggle('is-open', open);
  }

  async quickAdd(btn) {
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    const id = Number(btn.dataset.qaAdd);
    btn.setAttribute('aria-busy', 'true');
    const label = btn.innerHTML;
    try {
      let Cart = null;
      try { ({ Cart } = await import('@troop-themes/main')); } catch (err) { Cart = null; }
      if (Cart && typeof Cart.addItems === 'function') {
        await Cart.addItems({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: Cart.sections })
        }, btn);
      } else {
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] })
        });
        if (!res.ok) throw new Error(res.statusText);
        window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        return;
      }
      btn.classList.add('is-added');
      btn.textContent = btn.classList.contains('dtn-qa__one') ? 'Added' : '✓';
      setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = label; }, 1600);
    } catch (err) {
      btn.innerHTML = label;
      window.location.href = btn.closest('.dtn-card')?.querySelector('a[href]')?.href || window.location.href;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('dtn-collection')) customElements.define('dtn-collection', DtnCollection);
